import { X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type * as React from "react";

import { cn } from "#/lib/utils";

function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger(
	props: React.ComponentProps<typeof DialogPrimitive.Trigger>,
) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal(
	props: React.ComponentProps<typeof DialogPrimitive.Portal>,
) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn(
				"fixed inset-0 z-50 bg-[var(--color-theme-static-black)]/65 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
				className,
			)}
			{...props}
		/>
	);
}

function DialogContent({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
	return (
		<DialogPortal>
			<DialogOverlay />
			<DialogPrimitive.Content
				data-slot="dialog-content"
				className={cn(
					"fixed top-1/2 left-1/2 z-50 w-[min(960px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[var(--color-theme-line-default)] bg-[var(--color-theme-bg-elevated)] shadow-2xl outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
					className,
				)}
				{...props}
			/>
		</DialogPortal>
	);
}

function DialogTitle(
	props: React.ComponentProps<typeof DialogPrimitive.Title>,
) {
	return <DialogPrimitive.Title data-slot="dialog-title" {...props} />;
}

function DialogDescription(
	props: React.ComponentProps<typeof DialogPrimitive.Description>,
) {
	return (
		<DialogPrimitive.Description data-slot="dialog-description" {...props} />
	);
}

function DialogClose(
	props: React.ComponentProps<typeof DialogPrimitive.Close>,
) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

type DialogCloseButtonProps = React.ComponentProps<"button">;

function DialogCloseButton({
	className,
	children,
	type = "button",
	...props
}: DialogCloseButtonProps) {
	return (
		<DialogClose asChild>
			<button
				type={type}
				className={cn(
					"inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-trade-text-secondary transition-[color,background-color] hover:bg-trade-interactive hover:text-trade-text-strong disabled:opacity-50",
					className,
				)}
				{...{
					...props,
					"aria-label": props["aria-label"] ?? "Close",
				}}
			>
				{children ?? <X className="h-4 w-4" />}
			</button>
		</DialogClose>
	);
}

export {
	Dialog,
	DialogClose,
	DialogCloseButton,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
};
